"use client";

import { useEffect, useRef, type Ref } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { CmsCta } from "@/components/ui";
import { getStrapiMedia } from "@/lib/strapi/client";
import type { SpecialPopupType } from "@/lib/types/global-content";
import { useFocusTrap } from "@/hooks/useFocusTrap";

type Props = {
  data: SpecialPopupType | null;
  open: boolean;
  onClose: () => void;
};

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0 mt-0.5"
    >
      <path
        d="M3.5 8.25 6.5 11.25 12.5 4.75"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-brand-primary"
    >
      <path
        d="M2.5 9.2 8.7 3a1.4 1.4 0 0 1 2 0l2.3 2.3a1.4 1.4 0 0 1 0 2L6.8 13.5H2.5V9.2Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="10.15" cy="5.85" r="0.85" fill="currentColor" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      className="block"
    >
      <path
        d="M1.5 1.5l9 9M10.5 1.5l-9 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function SpecialPopup({ data, open, onClose }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const mounted = typeof document !== "undefined";

  useFocusTrap(panelRef, {
    active: open,
    onEscape: onClose,
    initialFocusRef: closeRef,
  });

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !data || !mounted) return null;

  const imageUrl = data.image?.url ? getStrapiMedia(data.image.url) : null;
  const tagIconUrl = data.tagIcon?.[0]?.url
    ? getStrapiMedia(data.tagIcon[0].url)
    : null;
  const highlights = data.highlights?.filter((h) => h.point?.trim()) ?? [];
  const buttons = data.buttons ?? [];
  const primaryButton =
    buttons.find((b) => b.label?.toLowerCase() !== "not now") ?? buttons[0];
  const dismissButton = buttons.find(
    (b) => b.label?.toLowerCase() === "not now",
  );
  const showTag = Boolean(data.tagLabel || tagIconUrl);

  const configuration = data.configuration;
  const alignment = configuration?.contentAlignment ?? "Left";
  const isCenter = alignment === "Center";
  const isRight = alignment === "Right";
  const contentAlignmentClass = isCenter
    ? "items-center text-center"
    : isRight
      ? "items-end text-right"
      : "items-start text-left";
  const backgroundColor = configuration?.backgroundColor ?? undefined;
  const backgroundPattern =
    configuration?.addBackgroundPattern && configuration.backgroundPattern?.url
      ? getStrapiMedia(configuration.backgroundPattern.url)
      : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-5 md:p-6 bg-brand-dark/90 backdrop-blur-[20%]"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={data.title ? 'special-popup-title' : undefined}
        aria-label={data.title ? undefined : 'Special offer'}
        tabIndex={-1}
        data-fig="1:1037"
        className="relative w-full max-w-[480px] max-h-[92svh] overflow-y-auto md:overflow-hidden bg-surface-white rounded-[24px] md:rounded-panel shadow-card"
        style={{ backgroundColor }}
        onClick={(e) => e.stopPropagation()}
      >
        {backgroundPattern && (
          <div
            className="absolute inset-0 z-0 pointer-events-none bg-cover bg-center bg-no-repeat rounded-[24px] md:rounded-panel"
            style={{ backgroundImage: `url("${backgroundPattern}")` }}
            aria-hidden="true"
          />
        )}

        {imageUrl && (
          <div className="relative z-10 h-[200px] w-full overflow-hidden rounded-t-[24px] md:rounded-t-panel">
            <Image
              src={imageUrl}
              alt={data.image?.alternativeText ?? data.title ?? "Special offer"}
              fill
              className="object-cover"
              sizes="(max-width: 991px) 100vw, 480px"
            />
            <div
              className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
              style={{
                backgroundImage: `linear-gradient(to top, ${backgroundColor ?? "#ffffff"}, transparent)`,
              }}
              aria-hidden="true"
            />
            <CloseButton ref={closeRef} onClose={onClose} />
          </div>
        )}

        {!imageUrl && <CloseButton ref={closeRef} onClose={onClose} />}

        <div
          className={`px-6 md:px-8 pb-6 md:pb-8 pt-0 md:-mt-5 relative z-10 flex flex-col ${contentAlignmentClass}`}
        >
          {showTag && (
            <div className="inline-flex items-center gap-2 rounded-pill border border-[#D8D2C6]/50 bg-white/90 backdrop-blur-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] px-3 py-1.5 -mt-5 md:mt-0 relative z-10">
              {tagIconUrl ? (
                <Image
                  src={tagIconUrl}
                  alt=""
                  width={14}
                  height={14}
                  className="object-contain"
                  aria-hidden="true"
                />
              ) : (
                <TagIcon />
              )}
              {data.tagLabel && (
                <span className="text-brand-dark text-xs font-body font-bold uppercase tracking-label">
                  {data.tagLabel}
                </span>
              )}
            </div>
          )}

          {data.title && (
            <h2
              id="special-popup-title"
              className="lg:text-card-title text-section-title font-heading text-text-dark mt-4"
            >
              {data.title}
            </h2>
          )}

          {data.description && (
            <p className="lg:text-card-meta text-body font-body text-text-primary leading-relaxed mt-3">
              {data.description}
            </p>
          )}

          {highlights.length > 0 && (
            <ul className="flex flex-col gap-3 mt-5" role="list">
              {highlights.map((item) => (
                <li
                  key={item.id}
                  className="flex items-start gap-3 text-body font-body text-text-primary"
                >
                  <span className="text-brand-primary">
                    <CheckIcon />
                  </span>
                  {item.point}
                </li>
              ))}
            </ul>
          )}

          {(primaryButton?.label || dismissButton?.label) && (
            <div
              className={`flex flex-col items-stretch md:flex-row md:items-center gap-4 md:gap-5 mt-6 pt-6 border-t border-border w-full ${
                isCenter
                  ? "md:justify-center"
                  : isRight
                    ? "md:justify-end"
                    : "md:justify-start"
              }`}
            >
              {primaryButton?.label && (
                <CmsCta
                  cta={primaryButton}
                  onClick={onClose}
                  showLinkArrow={false}
                  className="w-full md:w-auto cursor-pointer"
                />
              )}
              {dismissButton?.label && (
                <button
                  type="button"
                  onClick={onClose}
                  className={`cursor-pointer text-body font-body font-semibold text-text-dark hover:text-brand-primary transition-colors py-1 ${
                    isCenter
                      ? "text-center"
                      : isRight
                        ? "text-center md:text-right"
                        : "text-center md:text-left"
                  }`}
                >
                  {dismissButton.label}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CloseButton({
  onClose,
  ref,
}: {
  onClose: () => void;
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClose}
      aria-label="Close special offer"
      className="special-popup-close touch-target absolute top-4 right-4 z-10 inline-flex size-8 items-center justify-center rounded-full bg-surface-linen p-0 leading-none text-text-dark hover:bg-surface-linen/90 transition-colors cursor-pointer"
    >
      <CloseIcon />
    </button>
  );
}
